import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Table, type ColumnsType } from "@similarweb/pro-logic-components";

import { useAssignUsersToTeams } from "../../hooks/useAssignUsersToTeams";
import { useTeamsTrackingService } from "../../hooks/useTeamsTrackingService";
import type { AssignUserClickSource } from "../../services/tracking/factory";
import type { TeamListItem, TeamUser } from "../../types/teams.types";
import { AssignUsersToTeamPicker } from "../AssignUsersToTeamPicker/AssignUsersToTeamPicker";

import { filterAccountUserRows } from "./filterAccountUserRows";
import { getMinTableScrollWidth } from "./getMinTableScrollWidth";
import { SELECTION_COLUMN_WIDTH, StyledTeamAccountUsersTableWrapper } from "./styles";

const PAGE_SIZE = 20;
const ASSIGN_TO_TEAM_LABEL_KEY = "si.management.teams.users.bulk.assign_to_team";
const ASSIGN_TO_TEAM_DATA_AUTOMATION = "teams-management-assign-to-team";

type TeamAccountUsersTableProps<TRow extends TeamUser> = {
    rows: TRow[];
    teams: readonly TeamListItem[];
    columnConfig: ColumnsType<TRow>;
    searchPlaceholderKey: string;
    selectionTextKey: string;
    dataAutomation: string;
    getSearchableValues: (row: TRow) => readonly (string | null | undefined)[];
    assignClickSource: Extract<AssignUserClickSource, "assigned_bulk" | "unassigned_bulk">;
};

export const TeamAccountUsersTable = <TRow extends TeamUser>({
    rows,
    teams,
    columnConfig,
    searchPlaceholderKey,
    selectionTextKey,
    dataAutomation,
    getSearchableValues,
    assignClickSource,
}: TeamAccountUsersTableProps<TRow>) => {
    const { translate } = useTranslate();
    const tracking = useTeamsTrackingService();
    const { assignUsersToTeams, isPending: isAssignPending } = useAssignUsersToTeams();
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
    const [isAssignPickerOpen, setIsAssignPickerOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);

    const tableData = useMemo(
        () => filterAccountUserRows(rows, searchTerm, getSearchableValues),
        [rows, searchTerm, getSearchableValues],
    );

    const selectedRows = useMemo(
        () => rows.filter((row) => selectedUserIds.includes(row.userId)),
        [rows, selectedUserIds],
    );

    const minTableScrollWidth = useMemo(
        () => getMinTableScrollWidth(columnConfig, SELECTION_COLUMN_WIDTH),
        [columnConfig],
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    useEffect(() => {
        if (selectedUserIds.length === 0) {
            setIsAssignPickerOpen(false);
        }
    }, [selectedUserIds.length]);

    const handleSearchChange = useCallback((value: string) => {
        setSearchTerm(value);
    }, []);

    const handlePaginationChange = useCallback((_currentPage: number, _pageSize: number, nextPage: number) => {
        setCurrentPage(nextPage);
    }, []);

    const handleClearSelection = useCallback(() => {
        setSelectedUserIds([]);
    }, []);

    const handleAssignPickerOpenChange = useCallback((open: boolean) => {
        setIsAssignPickerOpen(open);
    }, []);

    const handleAssignToTeamClick = useCallback(() => {
        if (isAssignPending || isAssignPickerOpen) {
            return;
        }

        tracking.trackOpenAssignUserModal(assignClickSource);
        setIsAssignPickerOpen(true);
    }, [assignClickSource, isAssignPending, isAssignPickerOpen, tracking]);

    const selectionActionButtons = useMemo(
        () => [
            {
                iconName: "arrow-down-new" as const,
                type: "flat" as const,
                placement: "right" as const,
                children: translate(ASSIGN_TO_TEAM_LABEL_KEY),
                onClick: handleAssignToTeamClick,
                ref: triggerRef,
                isLoading: isAssignPending,
                isDisabled: isAssignPending,
                dataAutomation: ASSIGN_TO_TEAM_DATA_AUTOMATION,
            },
        ],
        [handleAssignToTeamClick, isAssignPending, translate],
    );

    return (
        <StyledTeamAccountUsersTableWrapper data-automation={dataAutomation}>
            <Table<TRow>
                data={tableData}
                rowKey="userId"
                columnConfig={columnConfig}
                scroll={{ x: minTableScrollWidth }}
                pagination={{
                    pageSize: PAGE_SIZE,
                    current: currentPage,
                    onChange: handlePaginationChange,
                }}
                withArrowButtons={false}
                selection={{
                    enabled: true,
                    columnWidth: SELECTION_COLUMN_WIDTH,
                    fixed: true,
                    selectedRowKeys: selectedUserIds,
                    onChange: (keys) => setSelectedUserIds(keys.map((key) => Number(key))),
                }}
            >
                <Table.TableTop borderBottom>
                    <Table.TableTop.Filters
                        filters={[
                            {
                                filter: "search",
                                initialValue: searchTerm,
                                onSearchChange: handleSearchChange,
                                placeholder: translate(searchPlaceholderKey),
                            },
                        ]}
                    />
                    <Table.TableTop.Selection
                        enabled
                        selectText={translate(selectionTextKey)}
                        actionButtons={selectionActionButtons}
                    />
                </Table.TableTop>
            </Table>

            <AssignUsersToTeamPicker
                teams={teams}
                users={selectedRows}
                isOpen={isAssignPickerOpen}
                onOpenChange={handleAssignPickerOpenChange}
                triggerRef={triggerRef}
                onSuccess={handleClearSelection}
                assignUsersToTeams={assignUsersToTeams}
                isPending={isAssignPending}
            />
        </StyledTeamAccountUsersTableWrapper>
    );
};
